// src/routes/categories/picker/CategoryBrowser.tsx
import { useState } from 'react'
import { CategoryIcon } from '../../../platform/icons/CategoryIcon'
import { IconGroupTabs } from '../../../platform/icons/IconGroupTabs'
import { Button } from '../../../platform/primitives/Button'
import { SearchField } from '../../../platform/primitives/SearchField'
import { useHitTarget } from '../../../platform/responsive/hitTarget'
import { useBreakpoint } from '../../../platform/responsive/useBreakpoint'
import { CATEGORY_TYPE_LABEL } from '../categoryTree'
import { groupKeyOf, searchPicker, type PickerGroup, type PickerItem, type PickerModel } from './pickerModel'
import './categoryPicker.css'

/**
 * The category picker's body — Option B of the 2026-10-13 design ("browse by
 * group"), chosen by the owner over search-first and inline suggestions.
 *
 * **Tiles are buttons, not radios.** A radiogroup moves its selection on the
 * arrow keys, and every caller but the bulk dialog closes on a pick — so the
 * first ↓ would set a category and shut the dialog. `aria-pressed` says which
 * one is current; Tab walks the tiles.
 *
 * **Search spans every group and replaces the rail while it is active**, as
 * `IconCataloguePicker` does, so a rail that looked selectable but did nothing
 * is never on screen. Enter picks the top result and is never a form submit.
 *
 * **Rail or tabs is `useBreakpoint()`'s call** (rule 5): a vertical rail beside
 * the tiles from tablet up; on a phone `IconGroupTabs`, without its "All" tab,
 * above a two-column grid.
 */
export function CategoryBrowser({
  model,
  value,
  onPick,
  autoFocus,
}: {
  model: PickerModel
  value: string | null
  onPick: (id: string) => void
  /** Focus the search field on mount — for a dialog whose data may land after the overlay's own focus move. */
  autoFocus?: boolean
}) {
  const phone = useBreakpoint() === 'phone'
  const target = useHitTarget('form')
  const [query, setQuery] = useState('')
  const [groupKey, setGroupKey] = useState<string | null>(() => groupKeyOf(model, value) ?? model.groups[0]?.key ?? null)

  const searching = query.trim() !== ''
  const results = searching ? searchPicker(model, query) : []
  const group = model.groups.find((g) => g.key === groupKey) ?? model.groups[0]

  const tiles = (items: PickerItem[], showParent: boolean, showType: boolean) => (
    <div className={phone ? 'category-tiles category-tiles--phone' : 'category-tiles'}>
      {items.map(({ category, groupName }) => (
        <button
          key={category.id}
          type="button"
          className="category-tile"
          style={{ minHeight: target }}
          aria-pressed={category.id === value}
          onClick={() => onPick(category.id)}
        >
          <span className="category-tile__glyph">
            <CategoryIcon category={category} size={22} />
          </span>
          <span className="category-tile__name">{category.name}</span>
          {showParent && groupName !== null && <span className="category-tile__sub">{groupName}</span>}
          {(showType || category.category_type !== 'expense') && (
            <span className={`category-type category-type--${category.category_type}`}>{CATEGORY_TYPE_LABEL[category.category_type]}</span>
          )}
        </button>
      ))}
    </div>
  )

  return (
    <div
      className="category-browser"
      onKeyDown={(e) => {
        if (e.key !== 'Enter' || !(e.target instanceof HTMLInputElement) || e.target.type !== 'search') return
        e.preventDefault()
        const top = results[0]
        if (top !== undefined) onPick(top.category.id)
      }}
    >
      <SearchField label="Search categories" placeholder={`Search ${model.count} categories`} value={query} onChange={setQuery} autoFocus={autoFocus} />

      {/* Mounted before it has text, so a change is announced (BulkToolbar's count pattern). */}
      <p role="status" className="category-browser__note">
        {searching ? (results.length === 0 ? `No category matches “${query.trim()}”.` : 'Results from every group.') : ''}
      </p>

      {searching ? (
        results.length === 0 ? (
          <div className="category-browser__empty">
            <Button type="button" variant="chip" size="form" onClick={() => setQuery('')}>
              Clear search
            </Button>
          </div>
        ) : (
          tiles(results, true, true)
        )
      ) : phone ? (
        <>
          <IconGroupTabs
            label="Category group"
            allLabel={null}
            groups={model.groups.map((g) => ({ key: g.key, label: g.label }))}
            value={group?.key ?? null}
            onChange={(k) => k !== null && setGroupKey(k)}
          />
          {group !== undefined && tiles(group.items, group.type === null, false)}
        </>
      ) : (
        <div className="category-browser__split">
          <Rail groups={model.groups} active={group?.key ?? null} onSelect={setGroupKey} minHeight={target} />
          {group !== undefined && tiles(group.items, group.type === null, false)}
        </div>
      )}
    </div>
  )
}

function Rail({ groups, active, onSelect, minHeight }: { groups: PickerGroup[]; active: string | null; onSelect: (k: string) => void; minHeight: number }) {
  return (
    <nav className="category-rail" aria-label="Category groups">
      {groups.map((g, i) => {
        const heading = g.type !== null && groups[i - 1]?.type !== g.type
        return (
          <div key={g.key} className="category-rail__entry">
            {heading && <span className="category-rail__heading">{CATEGORY_TYPE_LABEL[g.type as NonNullable<typeof g.type>]}</span>}
            <button
              type="button"
              className="category-rail__group"
              style={{ minHeight }}
              aria-pressed={g.key === active}
              onClick={() => onSelect(g.key)}
            >
              {g.label}
            </button>
          </div>
        )
      })}
    </nav>
  )
}
